import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';
import { 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Server,
  Zap,
  Globe,
  Activity,
  Radio,
  Eye,
  EyeOff,
  Heart,
  MessageSquare
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { sq } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import api from '../lib/api';

export default function OCPPLogs() {
  usePageTitle('OCPP Logs');
  
  // Connection logs state
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, connected: 0, unauthorized: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  
  // Message logs state
  const [messages, setMessages] = useState([]);
  const [messageStats, setMessageStats] = useState({ charger_stats: [], action_stats: [] });
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [messageFilter, setMessageFilter] = useState('all');
  const [messageChargerFilter, setMessageChargerFilter] = useState('');
  
  // Discovery mode state
  const [discoveryMode, setDiscoveryMode] = useState(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (statusFilter && statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      if (searchTerm) {
        params.append('charger_id', searchTerm);
      }
      
      const response = await api.get(`/admin/ocpp-logs?${params.toString()}`);
      setLogs(response.data.items || []);
      setStats(response.data.stats || { total: 0, connected: 0, unauthorized: 0, rejected: 0 });
    } catch (error) {
      console.error('Error fetching OCPP logs:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  const fetchMessages = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');
      if (messageFilter && messageFilter !== 'all') {
        params.append('action', messageFilter);
      }
      if (messageChargerFilter) {
        params.append('charger_id', messageChargerFilter);
      }
      
      const response = await api.get(`/admin/ocpp-messages?${params.toString()}`);
      setMessages(response.data.items || []);
      setMessageStats({
        charger_stats: response.data.charger_stats || [],
        action_stats: response.data.action_stats || []
      });
    } catch (error) {
      console.error('Error fetching OCPP messages:', error);
    } finally {
      setMessagesLoading(false);
    }
  }, [messageFilter, messageChargerFilter]);

  const fetchDiscoveryMode = async () => {
    try {
      const response = await api.get('/admin/ocpp-discovery-mode');
      setDiscoveryMode(response.data.discovery_mode);
    } catch (error) {
      console.error('Error fetching discovery mode:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
    fetchMessages();
    fetchDiscoveryMode();
  }, [fetchLogs, fetchMessages]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchLogs();
      fetchMessages();
    }, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchLogs, fetchMessages]);

  const clearLogs = async () => {
    try {
      await api.delete('/admin/ocpp-logs');
      toast.success('Logs u pastruan');
      fetchLogs();
    } catch (error) {
      toast.error('Gabim në pastrimin e logs');
    }
  };

  const clearMessages = async () => {
    try {
      await api.delete('/admin/ocpp-messages');
      toast.success('Mesazhet u pastruan');
      fetchMessages();
    } catch (error) {
      toast.error('Gabim në pastrimin e mesazheve');
    }
  };

  const toggleDiscoveryMode = async (enabled) => {
    setDiscoveryLoading(true);
    try {
      const response = await api.post(`/admin/ocpp-discovery-mode?enabled=${enabled}`);
      setDiscoveryMode(response.data.discovery_mode);
      toast.success(response.data.message);
    } catch (error) {
      toast.error('Gabim në ndryshimin e discovery mode');
    } finally {
      setDiscoveryLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'unauthorized':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'rejected':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Wifi className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'connected':
        return <Badge className="bg-green-500/20 text-green-500 border-green-500/30">Lidhur</Badge>;
      case 'unauthorized':
        return <Badge className="bg-amber-500/20 text-amber-500 border-amber-500/30">I Paregjistruar</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500/20 text-red-500 border-red-500/30">Refuzuar</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'Heartbeat':
        return <Heart className="h-4 w-4 text-red-400" />;
      case 'BootNotification':
        return <Zap className="h-4 w-4 text-yellow-400" />;
      case 'StatusNotification':
        return <Activity className="h-4 w-4 text-blue-400" />;
      case 'MeterValues':
        return <Radio className="h-4 w-4 text-green-400" />;
      default:
        return <MessageSquare className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getActionBadge = (action) => {
    const colors = {
      'Heartbeat': 'bg-red-500/20 text-red-400 border-red-500/30',
      'BootNotification': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'StatusNotification': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'MeterValues': 'bg-green-500/20 text-green-400 border-green-500/30',
      'StartTransaction': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'StopTransaction': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Authorize': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    };
    return <Badge className={colors[action] || 'bg-muted text-muted-foreground'}>{action}</Badge>;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-outfit flex items-center gap-2">
            <Server className="h-7 w-7 text-primary" />
            OCPP Monitoring
          </h1>
          <p className="text-muted-foreground mt-1">
            Monitoroni lidhjet dhe mesazhet OCPP nga karikuesit
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Discovery Mode Toggle */}
          <div className="flex items-center gap-2 px-3 py-2 bg-card border border-border rounded-lg">
            <div className="flex items-center gap-2">
              {discoveryMode ? (
                <Eye className="h-4 w-4 text-green-500" />
              ) : (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              )}
              <span className="text-sm text-foreground">Discovery Mode</span>
            </div>
            <Switch
              checked={discoveryMode}
              onCheckedChange={toggleDiscoveryMode}
              disabled={discoveryLoading}
            />
          </div>
          
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${autoRefresh ? 'animate-spin' : ''}`} />
            {autoRefresh ? 'Auto' : 'Manual'}
          </Button>
        </div>
      </div>

      {/* Discovery Mode Info */}
      {discoveryMode && (
        <Card className="bg-green-500/10 border-green-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-green-500" />
              <div>
                <p className="font-medium text-green-500">Discovery Mode Aktiv</p>
                <p className="text-sm text-muted-foreground">
                  Të gjithë karikuesit mund të lidhen tani, edhe ata të paregjistruar. 
                  Shiko tab-in "Mesazhet OCPP" për të parë heartbeat-et.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="connections" className="space-y-4">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="connections" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Wifi className="h-4 w-4 mr-2" />
            Lidhjet ({stats.total})
          </TabsTrigger>
          <TabsTrigger value="messages" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Heart className="h-4 w-4 mr-2" />
            Mesazhet OCPP ({messages.length})
          </TabsTrigger>
        </TabsList>

        {/* Connection Logs Tab */}
        <TabsContent value="connections" className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Total</p>
                    <p className="text-2xl font-bold text-foreground">{stats.total}</p>
                  </div>
                  <Wifi className="h-8 w-8 text-muted-foreground/30" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Lidhur</p>
                    <p className="text-2xl font-bold text-green-500">{stats.connected}</p>
                  </div>
                  <CheckCircle className="h-8 w-8 text-green-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Paregjistruar</p>
                    <p className="text-2xl font-bold text-amber-500">{stats.unauthorized}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-amber-500/30" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground">Refuzuar</p>
                    <p className="text-2xl font-bold text-red-500">{stats.rejected}</p>
                  </div>
                  <XCircle className="h-8 w-8 text-red-500/30" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Kërko sipas Charger ID..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-background"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-[180px] bg-background">
                    <SelectValue placeholder="Filtro sipas statusit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjitha</SelectItem>
                    <SelectItem value="connected">Lidhur</SelectItem>
                    <SelectItem value="unauthorized">Paregjistruar</SelectItem>
                    <SelectItem value="rejected">Refuzuar</SelectItem>
                  </SelectContent>
                </Select>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Pastro
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Pastro Logs?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Kjo do të fshijë të gjitha logs e lidhjeve OCPP. Ky veprim nuk mund të kthehet.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Anulo</AlertDialogCancel>
                      <AlertDialogAction onClick={clearLogs} className="bg-red-500 hover:bg-red-600">
                        Pastro
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground font-outfit flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Historiku i Lidhjeve
              </CardTitle>
              <CardDescription>
                {autoRefresh && <span className="text-green-500">● </span>}
                Përditësohet automatikisht çdo 5 sekonda
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : logs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <WifiOff className="h-12 w-12 mb-4 opacity-30" />
                  <p>Nuk ka logs të lidhjeve</p>
                  <p className="text-sm mt-2">Kur një karikues tenton të lidhet, do të shfaqet këtu</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Statusi</TableHead>
                        <TableHead>Charger ID</TableHead>
                        <TableHead>OCPP Version</TableHead>
                        <TableHead>IP Adresa</TableHead>
                        <TableHead>Arsyeja</TableHead>
                        <TableHead>Koha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log, index) => (
                        <TableRow key={log.id || index} className={log.status === 'unauthorized' ? 'bg-amber-500/5' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getStatusIcon(log.status)}
                              {getStatusBadge(log.status)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Zap className="h-4 w-4 text-primary" />
                              <span className="font-mono font-medium">{log.charger_id}</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{log.ocpp_version}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Globe className="h-4 w-4" />
                              <span className="font-mono text-sm">{log.remote_ip}</span>
                            </div>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                            {log.reason}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {log.timestamp ? formatDistanceToNow(new Date(log.timestamp), { addSuffix: true, locale: sq }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* OCPP Messages Tab */}
        <TabsContent value="messages" className="space-y-4">
          {/* Message Stats */}
          {messageStats.charger_stats.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-foreground">Karikuesit Aktivë</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {messageStats.charger_stats.slice(0, 5).map((stat, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <span className="font-mono text-foreground">{stat._id}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{stat.message_count} mesazhe</span>
                          <span className="text-xs text-green-500">
                            {stat.last_message ? formatDistanceToNow(new Date(stat.last_message), { addSuffix: true, locale: sq }) : '-'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-foreground">Llojet e Mesazheve</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    {messageStats.action_stats.map((stat, index) => (
                      <div key={index} className="flex items-center gap-1">
                        {getActionBadge(stat.action)}
                        <span className="text-xs text-muted-foreground">({stat.count})</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Message Filters */}
          <Card className="bg-card border-border">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Input
                    placeholder="Filtro sipas Charger ID..."
                    value={messageChargerFilter}
                    onChange={(e) => setMessageChargerFilter(e.target.value)}
                    className="bg-background"
                  />
                </div>
                <Select value={messageFilter} onValueChange={setMessageFilter}>
                  <SelectTrigger className="w-full sm:w-[200px] bg-background">
                    <SelectValue placeholder="Filtro sipas tipit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Të gjitha</SelectItem>
                    <SelectItem value="Heartbeat">Heartbeat</SelectItem>
                    <SelectItem value="BootNotification">BootNotification</SelectItem>
                    <SelectItem value="StatusNotification">StatusNotification</SelectItem>
                    <SelectItem value="MeterValues">MeterValues</SelectItem>
                    <SelectItem value="StartTransaction">StartTransaction</SelectItem>
                    <SelectItem value="StopTransaction">StopTransaction</SelectItem>
                    <SelectItem value="Authorize">Authorize</SelectItem>
                  </SelectContent>
                </Select>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Pastro
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Pastro Mesazhet?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Kjo do të fshijë të gjitha mesazhet OCPP. Ky veprim nuk mund të kthehet.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Anulo</AlertDialogCancel>
                      <AlertDialogAction onClick={clearMessages} className="bg-red-500 hover:bg-red-600">
                        Pastro
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardContent>
          </Card>

          {/* Messages Table */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground font-outfit flex items-center gap-2">
                <Heart className="h-5 w-5 text-red-400" />
                Mesazhet OCPP (Heartbeat, Boot, Status...)
              </CardTitle>
              <CardDescription>
                {autoRefresh && <span className="text-green-500">● </span>}
                Shfaq mesazhet e fundit nga karikuesit e lidhur
              </CardDescription>
            </CardHeader>
            <CardContent>
              {messagesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Heart className="h-12 w-12 mb-4 opacity-30" />
                  <p>Nuk ka mesazhe OCPP</p>
                  <p className="text-sm mt-2">
                    {discoveryMode 
                      ? "Prisni që karikuesi të dërgojë mesazhe (Heartbeat, Boot...)" 
                      : "Aktivizo Discovery Mode për të pranuar karikues të paregjistruar"}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Veprimi</TableHead>
                        <TableHead>Charger ID</TableHead>
                        <TableHead>OCPP</TableHead>
                        <TableHead>Payload</TableHead>
                        <TableHead>IP</TableHead>
                        <TableHead>Koha</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {messages.map((msg, index) => (
                        <TableRow key={msg.id || index} className={msg.action === 'Heartbeat' ? 'bg-red-500/5' : ''}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getActionIcon(msg.action)}
                              {getActionBadge(msg.action)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono font-medium text-foreground">{msg.charger_id}</span>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{msg.ocpp_version}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[250px]">
                            <code className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded truncate block">
                              {JSON.stringify(msg.payload || {}).substring(0, 100)}
                              {JSON.stringify(msg.payload || {}).length > 100 && '...'}
                            </code>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-xs text-muted-foreground">{msg.remote_ip}</span>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                            {msg.timestamp ? formatDistanceToNow(new Date(msg.timestamp), { addSuffix: true, locale: sq }) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Help Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-foreground font-outfit">Si të shtoni një karikues të ri?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-background rounded-lg border border-border">
              <h4 className="font-medium text-foreground mb-2">OCPP 1.6J</h4>
              <code className="text-sm text-green-500 bg-green-500/10 px-2 py-1 rounded block break-all">
                wss://ocpp.albmove.al/1.6/CHARGER_ID
              </code>
            </div>
            <div className="p-4 bg-background rounded-lg border border-border">
              <h4 className="font-medium text-foreground mb-2">OCPP 2.0.1</h4>
              <code className="text-sm text-blue-500 bg-blue-500/10 px-2 py-1 rounded block break-all">
                wss://ocpp.albmove.al/2.0.1/CHARGER_ID
              </code>
            </div>
          </div>
          <div className="text-sm text-muted-foreground space-y-2">
            <p><strong>Hapi 1:</strong> Aktivizo "Discovery Mode" për të pranuar karikues të paregjistruar</p>
            <p><strong>Hapi 2:</strong> Vendosni URL-në OCPP në karikuesin tuaj dhe riniseni</p>
            <p><strong>Hapi 3:</strong> Shikoni tab-in "Mesazhet OCPP" për të parë Heartbeat dhe BootNotification</p>
            <p><strong>Hapi 4:</strong> Kur të shihni karikuesin, shkoni te Karikuesit → Shto Karikues me të njëjtin Charger ID</p>
            <p><strong>Hapi 5:</strong> Çaktivizo "Discovery Mode" pasi të keni regjistruar karikuesin</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
